/**
 * CREATIVE DETONATOR - Image Generation Module
 * Handles image generation for idea cards using fal.ai
 *
 * Features:
 * - Modal for model/aspect selection and prompt editing
 * - Queue-based generation with polling
 * - Parallel generation support
 * - 8-bit loading placeholder
 * - Image display in cards
 * - Batch generation for selected ideas
 * - Auto generation mode (blocking)
 * - Style dropdown with multiple presets
 * - Image preview modal and download
 */

// v70 - Fixed: History button now included in showGeneratedImage() template
import { GeneratorState, API_CONFIG, persistSessionState } from '../core/state.js';
import { getSessionToken } from '../../auth/js/client.js';
import { initImagePreview, openPreview, closePreview } from '../../../js/shared/image-preview.js';
// Task 113: Added addPanelToPool to sync generated frames to panel library
import { setSceneFrame, getActiveStoryboard, setStoryboard, addPanelToPool } from '../storyboard/state.js';
import { injectModalHtml } from '../../../js/shared/dom-utils.js';
import { renderHistoryButton } from './version-history.js';
import { onDocument, cleanupScope, emit, ImageEvents, IdeaEvents } from '../../../js/core/events.js';
import { IMAGE_EVENTS, IDEA_EVENTS } from '../../../js/core/event-types.js';
import { showError } from '../../../js/core/error-handler.js';
import { createLogger } from '../../../js/core/logger.js';
import { getContextBarHTML, updateContextBar, setupContextToggle, ContextState } from '../../../js/shared/modal-context-bar.js';
import { downloadImage as downloadImageCore } from '../../../js/core/download.js';
import { animateCoinSpend, updateCreditDisplays } from '../../../js/shared/credit-animation.js';
// Centralized pricing - Single Source of Truth (see frontend/js/core/pricing.js)
import { IMAGE_COSTS, getImageCost, formatCredits } from '../../../js/core/pricing.js';

const log = createLogger('ImageGen');

// CREDIT_COSTS alias for backwards compatibility (now imported from pricing.js)
const CREDIT_COSTS = IMAGE_COSTS;

/**
 * Refresh credit balance from server and update all displays
 */
async function refreshCreditsFromServer() {
  try {
    const token = await getSessionToken();
    if (!token) return;

    const response = await fetch(`${API_CONFIG.baseUrl}/api/credits`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && typeof data.credits === 'number') {
        updateCreditDisplays(data.credits);
        log.debug('Credits refreshed:', data.credits);
      }
    }
  } catch (err) {
    log.warn('Failed to refresh credits:', err.message);
  }
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Check if a value is a valid string (not null, undefined, empty, or literal "null")
 * Used to prevent "null" strings from appearing in image prompts
 * @param {any} value - The value to check
 * @returns {boolean} - True if value is a valid non-empty string
 */
function isValidString(value) {
  return value &&
         typeof value === 'string' &&
         value.trim() !== '' &&
         value.toLowerCase() !== 'null';
}

/**
 * Promise wrapper with timeout - prevents infinite hangs
 * @param {Promise} promise - The promise to wrap
 * @param {number} ms - Timeout in milliseconds
 * @param {string} errorMsg - Error message on timeout
 * @returns {Promise}
 */
function withTimeout(promise, ms, errorMsg = 'Operation timed out') {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(errorMsg)), ms))
  ]);
}

// ============================================================================
// HTML TEMPLATE - Self-rendering modal (window-titlebar version)
// ============================================================================

const MODAL_HTML = `
<div class="modal-overlay" id="image-gen-modal">
  <div class="retro-modal image-gen-modal">
    <div class="window-titlebar modal-titlebar">
      <div class="window-close modal-close-btn" id="image-gen-close" aria-label="Close modal"></div>
      <div class="window-title">◆ GENERATE IMAGE</div>
    </div>
    <div class="modal-idea-subtitle" id="image-gen-idea-title"></div>
    <div class="modal-body">
      <div class="image-gen-section">
        <label class="image-gen-label">MODEL</label>
        <div class="model-selector" id="model-selector"></div>
      </div>
      <div class="image-gen-section">
        <label class="image-gen-label">ASPECT RATIO</label>
        <div class="aspect-selector" id="aspect-selector">
          <button class="aspect-btn" data-ratio="16:9">16:9</button>
          <button class="aspect-btn active" data-ratio="4:3">4:3</button>
          <button class="aspect-btn" data-ratio="3:2">3:2</button>
          <button class="aspect-btn" data-ratio="1:1">1:1</button>
          <button class="aspect-btn" data-ratio="3:4">3:4</button>
          <button class="aspect-btn" data-ratio="9:16">9:16</button>
        </div>
      </div>
      <div class="image-gen-section">
        <label class="image-gen-label">PROMPT</label>
        <textarea class="image-gen-textarea" id="image-gen-prompt" rows="6" spellcheck="false"></textarea>
      </div>
      <div class="image-gen-section">
        <label class="image-gen-label">STYLE</label>
        <select class="image-gen-select" id="style-selector">
          <option value="auto">Auto (No Style)</option>
          <option value="advertising">Advertising</option>
          <option value="editorial">Editorial</option>
          <option value="minimal">Minimal</option>
          <option value="bold">Bold</option>
          <option value="8bit-bw">8-Bit B&W</option>
          <option value="random">Random</option>
        </select>
      </div>
      ${getContextBarHTML('image-gen')}
      <div class="image-gen-cost" id="image-gen-cost">Est. cost: <span class="cost-value">$0.04</span> <span class="cost-credits">(5 C)</span></div>
      <div class="prompt-tip-blue">
        <span class="tip-icon-blue">i</span>
        <span class="tip-text-blue">Image will appear in the idea card. You can generate multiple images in parallel.</span>
      </div>
    </div>
    <div class="modal-footer image-gen-footer">
      <button class="btn btn-ghost" id="image-gen-cancel">CANCEL</button>
      <button class="btn btn-primary btn-generate-image" id="image-gen-submit">
        <span class="btn-text">GENERATE <span class="btn-credits" id="image-gen-btn-credits"><span class="pixel-coin-static xs"></span> <span class="btn-credits-value">5</span></span></span>
        <span class="btn-loading" style="display:none;">GENERATING...</span>
      </button>
    </div>
  </div>
</div>
`;

// ============================================================================
// STYLE GUIDES
// ============================================================================

export const STYLE_GUIDES = {
  auto: '', // No style text added
  advertising: `You are an elite Creative Director at a top agency, art director and copywriter with decades of experience in creating advertising.

YOUR JOB: Make it better. Not different. BETTER.

## HOW TO THINK

Is this a good ad? Do we show the product in an unexpected situation that shows the benefit? Think like an experienced art director and visual designer and make the layout work and show whats needed to communicate the benefit. Your creativity goes into: WHERE it appears, WHO reacts, WHAT moment you capture.

## YOUR TOOLKIT

**Visual Fixes:**
- Is the visual doing enough work? Or is it decorative? Think like an experienced art director and visual designer and make the layout work and show whats needed.
- What do we see, how it the typography, colors, deign of the whole thing.

**Headline Fixes:**
- Is it earning its place? Or just sitting there?
- Does it add tension to the visual? Or repeat it?
- What is the best font and size and placing to communicate the message?

## THE RULE: SIMPLICITY KILLS

Great ads have ONE visual idea. Not a scene. Not a story. ONE THING.

Ask yourself:
- Does it catch the viewer.
- Does it work as a THUMBNAIL? If I squint, do I get it?

## WHAT YOU'RE MAKING

A POSTER. Not a stock photo. Not a film still. A POSTER.

- The image IS the idea, not an illustration of the idea

## THE VISUAL TEST

Before writing any visual, answer:
1. What's the ONE unexpected thing we're showing?
2. Can a stranger understand it in 2 seconds with no headline?`,
  editorial: 'High-end editorial photography. Magazine quality, artistic composition.',
  minimal: 'Minimalist aesthetic. Clean backgrounds, focus on subject.',
  bold: 'Bold and impactful visual. High contrast, attention-grabbing.',
  '8bit-bw': 'Super minimalistic black and white 8-bit pixel art style. Black and white only. Retro video game aesthetic. Chunky pixels, limited shading.'
  // Note: 'random' is handled separately in getEffectiveStyle()
};

// Styles that can be randomly selected (excludes 'auto')
const RANDOMIZABLE_STYLES = ['advertising', 'editorial', 'minimal', 'bold', '8bit-bw'];

/**
 * Get effective style - handles 'random' by picking a random style
 * @param {string} style - The requested style ('auto', 'random', or a specific style)
 * @returns {string} - The actual style to use
 */
export function getEffectiveStyle(style) {
  if (style === 'random') {
    const randomIndex = Math.floor(Math.random() * RANDOMIZABLE_STYLES.length);
    const selectedStyle = RANDOMIZABLE_STYLES[randomIndex];
    log.debug(` Random style selected: ${selectedStyle}`);
    return selectedStyle;
  }
  return style;
}

// ============================================================================
// STATE
// ============================================================================

// Image generation state (per idea)
const imageGenerations = {};

// Active polling intervals
const pollingIntervals = {};

// Providers cache
let providersCache = null;

// LocalStorage key for persisting active image generations
const ACTIVE_IMAGE_GENERATIONS_KEY = 'cd_active_image_generations';

// ============================================================================
// IMAGE GENERATION PERSISTENCE (Fix for page refresh losing in-progress generations)
// ============================================================================

/**
 * Save active generation to localStorage for recovery after page refresh
 * @param {string} genKey - Composite generation key
 * @param {string} modelId - The model ID
 * @param {string} requestId - The request ID
 * @param {Object} meta - Metadata including ideaId, frameTarget, provider, aspectRatio
 */
function saveActiveImageGeneration(genKey, modelId, requestId, meta = {}) {
  try {
    const stored = localStorage.getItem(ACTIVE_IMAGE_GENERATIONS_KEY);
    const generations = stored ? JSON.parse(stored) : {};

    generations[genKey] = {
      modelId,
      requestId,
      startTime: Date.now(),
      meta
    };

    localStorage.setItem(ACTIVE_IMAGE_GENERATIONS_KEY, JSON.stringify(generations));
    log.debug('Persisted active image generation for', genKey);
  } catch (e) {
    log.warn('Could not persist active image generation:', e.message);
  }
}

/**
 * Remove completed/failed generation from localStorage
 * @param {string} genKey - Composite generation key
 */
function clearActiveImageGeneration(genKey) {
  try {
    const stored = localStorage.getItem(ACTIVE_IMAGE_GENERATIONS_KEY);
    if (!stored) return;

    const generations = JSON.parse(stored);
    delete generations[genKey];
    localStorage.setItem(ACTIVE_IMAGE_GENERATIONS_KEY, JSON.stringify(generations));
    log.debug('Cleared active image generation for', genKey);
  } catch (e) {
    log.warn('Could not clear active image generation:', e.message);
  }
}

/**
 * Load and resume any active image generations from localStorage
 * Called on init to recover images after page refresh
 */
async function resumeActiveImageGenerations() {
  try {
    const stored = localStorage.getItem(ACTIVE_IMAGE_GENERATIONS_KEY);
    if (!stored) return;

    const generations = JSON.parse(stored);
    const genCount = Object.keys(generations).length;

    if (genCount === 0) return;

    log.info(`Found ${genCount} active image generation(s) to resume...`);

    const GENERATION_TIMEOUT = 300000; // 5 minutes max for images
    const now = Date.now();

    for (const [genKey, gen] of Object.entries(generations)) {
      const ideaId = gen.meta?.ideaId || genKey;

      // Check if generation is too old (probably failed/orphaned)
      if (now - gen.startTime > GENERATION_TIMEOUT) {
        log.debug('Skipping expired image generation for', genKey);
        clearActiveImageGeneration(genKey);
        continue;
      }

      // Skip if already polling
      if (pollingIntervals[genKey]) {
        log.debug('Already polling', genKey, ', skipping resume');
        continue;
      }

      log.info('Resuming image polling for', genKey, '(model:', gen.modelId, ')');

      // Restore state
      imageGenerations[genKey] = {
        status: 'IN_QUEUE',
        requestId: gen.requestId,
        modelId: gen.modelId,
        provider: gen.meta?.provider,
        aspectRatio: gen.meta?.aspectRatio,
        ideaId: ideaId,
        frameTarget: gen.meta?.frameTarget
      };

      // Show placeholder
      if (gen.meta?.frameTarget) {
        showFramePlaceholder(gen.meta.frameTarget.sceneSeq, gen.meta.frameTarget.frameType);
      } else {
        showImagePlaceholder(ideaId);
      }

      // Resume polling
      startPolling(genKey, gen.modelId, gen.requestId);
    }
  } catch (e) {
    log.warn('Could not resume active image generations:', e.message);
    localStorage.removeItem(ACTIVE_IMAGE_GENERATIONS_KEY);
  }
}

// Current idea being edited in modal
let currentIdeaId = null;

// Scene frame target (when generating for a specific scene frame)
let sceneFrameTarget = null; // { ideaId, sceneSeq, frameType }

// ============================================================================
// COMPOSITE KEY GENERATION (Task 102: Fix concurrent frame generation collisions)
// ============================================================================

/**
 * Generate a unique key for tracking concurrent frame generations
 * Prevents state collisions when multiple scene frames generate simultaneously
 * @param {string} ideaId - The idea ID
 * @param {Object|null} frameTarget - Frame target if generating for a scene frame
 * @returns {string} - Composite key for state tracking
 */
function getFrameGenerationKey(ideaId, frameTarget = null) {
  if (frameTarget?.sceneSeq != null && frameTarget?.frameType) {
    return `${ideaId}_frame_${frameTarget.sceneSeq}_${frameTarget.frameType}`;
  }
  return String(ideaId);
}

/**
 * Extract ideaId from a composite frame key
 * @param {string} genKey - The composite generation key
 * @returns {string} - The original idea ID
 */
function extractIdeaIdFromFrameKey(genKey) {
  // Handle both "uuid" and "uuid_frame_N_type" formats
  const parts = genKey.split('_frame_');
  return parts[0];
}

// ============================================================================
// API ENDPOINTS
// ============================================================================

const IMAGE_GEN_ENDPOINTS = {
  providers: '/api/image-gen/providers',
  generate: '/api/image-gen/generate',
  submit: '/api/image-gen/submit',
  status: (modelId, requestId) => `/api/image-gen/status/${encodeURIComponent(modelId)}/${requestId}`,
  result: (modelId, requestId) => `/api/image-gen/result/${encodeURIComponent(modelId)}/${requestId}`
};

function getApiUrl(endpoint) {
  return API_CONFIG.baseUrl + endpoint;
}

/**
 * Get headers with auth token for API calls
 * With 5 second timeout to prevent hanging on auth issues
 * Caches token in GeneratorState to avoid Supabase auth deadlock on repeated polls
 * @returns {Promise<Object>} Headers object with Content-Type and Authorization
 */
let _tokenFetchInFlight = null; // Prevent concurrent getSessionToken calls

async function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };

  try {
    // First try cached token from GeneratorState (set during idea generation or previous fetch)
    // This avoids Supabase auth deadlock issues (see VERSION-HISTORY-AUTH-BUGS.md)
    let token = GeneratorState.cachedAuthToken;

    if (token) {
      log.debug('Using cached auth token from GeneratorState');
    } else {
      // No cached token - try to get fresh one with 5s timeout
      // Use in-flight guard to prevent concurrent Supabase calls that deadlock
      log.debug('No cached token, fetching fresh token...');
      if (!_tokenFetchInFlight) {
        _tokenFetchInFlight = withTimeout(
          getSessionToken(),
          5000,
          'Auth token request timed out'
        ).finally(() => { _tokenFetchInFlight = null; });
      }
      token = await _tokenFetchInFlight;

      // Cache the token so subsequent calls (e.g. polling) don't re-fetch
      if (token) {
        GeneratorState.cachedAuthToken = token;
        log.debug('Cached fresh auth token in GeneratorState');
      }
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (error) {
    log.warn('Failed to get auth token:', error.message);
    _tokenFetchInFlight = null;
    // Return headers without auth - backend will reject but frontend won't hang
  }

  return headers;
}

// ============================================================================
// ENSURE IDEA SAVED (Fix for Harvest Phase ID Mismatch)
// ============================================================================

/**
 * Check if a string is a valid UUID (database ID)
 * @param {string} id - The ID to check
 * @returns {boolean}
 */
function isValidUUID(id) {
  if (!id || typeof id !== 'string') return false;
  // UUID v4 pattern: 8-4-4-4-12 hex chars
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/**
 * Ensure an idea is saved to the database before generating images
 * This prevents the "ID mismatch" issue where frontend IDs (like A1-1735...)
 * can't be linked to images because no UUID exists yet.
 *
 * @param {string} ideaId - The frontend ID or UUID
 * @returns {Promise<string|null>} - The database UUID, or null if save failed
 */
async function ensureIdeaSaved(ideaId) {
  // Already a valid UUID - return as-is
  if (isValidUUID(ideaId)) {
    log.debug(' ID is already a valid UUID:', ideaId);
    return ideaId;
  }

  // Check if we have a mapping in memory
  if (GeneratorState.idMapping?.[ideaId]) {
    const dbId = GeneratorState.idMapping[ideaId];
    log.debug(' Found ID in memory mapping:', ideaId, '→', dbId);
    return dbId;
  }

  // Check sessionStorage
  try {
    const stored = sessionStorage.getItem('cd_id_mapping');
    if (stored) {
      const mapping = JSON.parse(stored);
      if (mapping[ideaId]) {
        const dbId = mapping[ideaId];
        // Also store in memory for future use
        if (!GeneratorState.idMapping) GeneratorState.idMapping = {};
        GeneratorState.idMapping[ideaId] = dbId;
        log.debug(' Found ID in sessionStorage:', ideaId, '→', dbId);
        return dbId;
      }
    }
  } catch (e) {
    log.warn(' Could not read id mapping from sessionStorage:', e);
  }

  // No mapping found - need to save the idea first
  log.warn(' No UUID found for idea:', ideaId, '- attempting to save idea to database');

  // Find the idea in GeneratorState
  const idea = findIdeaById(ideaId);
  if (!idea) {
    log.error(' Cannot find idea to save:', ideaId);
    return null;
  }

  // Try to save via API
  try {
    const { api } = await import('../../../js/api/client.js');

    // Build the idea data for saving
    const ideaData = {
      format: idea.format || 'print',
      trigger_code: idea.triggerCode || idea.trigger_code || idea.trigger || '',
      headline: idea.headline || idea.title || '',
      headline_label: idea.headlineLabel || idea.headline_label || 'HEADLINE',
      subline: idea.subline || '',
      visual_description: idea.text || idea.visual || idea.visual_description || '',
      why_it_works: idea.whyItWorks || idea.why_it_works || idea.mechanism || '',
      session_id: GeneratorState.session?.id || null
    };

    log.debug(' Saving idea to database...', ideaData);

    // Note: We need an endpoint that creates an idea and returns its UUID
    // Using updateIdea won't work for new ideas. Let's try the manual idea endpoint
    // or check if there's a create endpoint
    const result = await api.createManualIdea(ideaData);

    if (result.success && result.idea?.id) {
      const dbId = result.idea.id;

      // Store the mapping
      if (!GeneratorState.idMapping) GeneratorState.idMapping = {};
      GeneratorState.idMapping[ideaId] = dbId;

      // Also update sessionStorage
      try {
        const stored = sessionStorage.getItem('cd_id_mapping') || '{}';
        const mapping = JSON.parse(stored);
        mapping[ideaId] = dbId;
        sessionStorage.setItem('cd_id_mapping', JSON.stringify(mapping));
      } catch (e) {
        log.warn(' Could not persist id mapping:', e);
      }

      // Update the idea object with the new ID
      idea.dbId = dbId;

      log.info(' Idea saved successfully:', ideaId, '→', dbId);
      return dbId;
    } else {
      log.error(' Failed to save idea:', result.error || 'Unknown error');
      return null;
    }
  } catch (error) {
    log.error(' Error saving idea:', error.message);
    return null;
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize image generation module
 * Called from phase4-harvest.js
 */
export async function initImageGeneration() {
  // Self-render: inject modal HTML if not already present
  injectModalHtml('image-gen-modal', MODAL_HTML);

  // Load providers from backend
  await loadProviders();

  // Initialize modal event listeners
  initModalEvents();

  // Initialize card button event delegation
  initCardButtonEvents();

  // Resume any in-progress generations (page refresh recovery)
  await resumeActiveImageGenerations();
}

/**
 * Load available providers from backend
 */
async function loadProviders() {
  try {
    const response = await fetch(getApiUrl(IMAGE_GEN_ENDPOINTS.providers));
    const data = await response.json();

    if (data.success && data.providers) {
      providersCache = data.providers;
      renderModelSelector();
    }
  } catch (error) {
    log.error('Failed to load image gen providers:', error);
    // Use fallback defaults
    providersCache = [
      { id: 'seedream', name: 'ByteDance SeedReam v4.5', shortName: 'SeedReam', costPerImage: 0.04 },
      { id: 'nano-banana', name: 'Nano Banana Pro', shortName: 'Nano Banana', costPerImage: 0.15 },
      { id: 'flux2-flash', name: 'Flux 2 Flash', shortName: 'Flux 2', costPerImage: 0.005 }
    ];
    renderModelSelector();
  }
}

/**
 * Render model selector buttons in modal
 * IMPORTANT: Flux 2 Flash is auto-only (via AUTO_IMAGE_PROVIDER env), NOT shown in modal
 */
function renderModelSelector() {
  const container = document.getElementById('model-selector');
  if (!container || !providersCache) return;

  // Filter out auto-only providers (flux2-flash is for AUTO_IMAGE_PROVIDER env only)
  const modalProviders = providersCache.filter(p => p.id !== 'flux2-flash');

  container.innerHTML = modalProviders.map((provider, index) => `
    <button class="model-btn ${index === 0 ? 'active' : ''}"
            data-provider="${provider.id}"
            data-cost="${provider.costPerImage}">
      <span class="model-name">${provider.shortName}</span>
      <span class="model-cost">$${provider.costPerImage.toFixed(2)}</span>
    </button>
  `).join('');

  // Add click handlers
  container.querySelectorAll('.model-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      container.querySelectorAll('.model-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      updateCostDisplay();
    });
  });
}

/**
 * Update cost display based on selected model
 * FIX UI-3: Also show credits alongside USD
 * FIX UI-4: Update button credit badge as well
 * FIX UI-5: Use CREDIT_COSTS lookup instead of calculating from USD
 */
function updateCostDisplay() {
  const activeModel = document.querySelector('.model-btn.active');
  const costDisplay = document.querySelector('#image-gen-cost .cost-value');
  const creditsDisplay = document.querySelector('#image-gen-cost .cost-credits');
  const btnCreditsValue = document.querySelector('#image-gen-btn-credits .btn-credits-value');

  if (activeModel && costDisplay) {
    const cost = parseFloat(activeModel.dataset.cost);
    const providerId = activeModel.dataset.provider;
    costDisplay.textContent = `$${cost.toFixed(2)}`;

    // FIX UI-5: Look up actual credit cost from CREDIT_COSTS constant
    const credits = CREDIT_COSTS[providerId] || CREDIT_COSTS['seedream'] || 5;
    if (creditsDisplay) {
      creditsDisplay.textContent = `(${credits} C)`;
    }

    // FIX UI-4: Update button credit badge
    if (btnCreditsValue) {
      btnCreditsValue.textContent = credits;
    }
  }
}

// ============================================================================
// MODAL HANDLING
// ============================================================================

/**
 * Initialize modal event listeners
 */
function initModalEvents() {
  const modal = document.getElementById('image-gen-modal');
  if (!modal) {
    log.warn('initModalEvents: Modal not found!');
    return;
  }

  // Close buttons
  document.getElementById('image-gen-close')?.addEventListener('click', closeImageGenModal);
  document.getElementById('image-gen-cancel')?.addEventListener('click', closeImageGenModal);

  // Submit button
  const submitBtn = document.getElementById('image-gen-submit');
  if (submitBtn) {
    submitBtn.addEventListener('click', submitImageGeneration);
    log.info('Submit button listener attached');
  } else {
    log.warn('Submit button not found!');
  }

  // Overlay click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeImageGenModal();
    }
  });

  // Aspect ratio buttons - update prompt when ratio changes
  document.getElementById('aspect-selector')?.addEventListener('click', (e) => {
    const btn = e.target.closest('.aspect-btn');
    if (btn) {
      document.querySelectorAll('.aspect-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      // Refresh prompt with new aspect ratio and style
      refreshPromptWithSettings();
    }
  });

  // Style dropdown - update prompt when style changes
  document.getElementById('style-selector')?.addEventListener('change', () => {
    refreshPromptWithSettings();
  });

  // Escape key (scoped for cleanup)
  onDocument('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeImageGenModal();
    }
  }, 'image-gen-modal');
}

/**
 * Initialize card button event delegation
 * Uses document-level delegation with flag to prevent duplicate bindings
 */
function initCardButtonEvents() {
  // Prevent duplicate event binding
  if (window._imgGenEventsInitialized) {
    log.debug(' Event handlers already initialized');
    return;
  }

  log.debug(' Initializing card button event handlers');

  // Event delegation on document.body for dynamically created buttons
  document.body.addEventListener('click', (e) => {
    // Generate image button
    const generateBtn = e.target.closest('.btn-generate-img');
    if (generateBtn) {
      // Skip if Dashboard already handled this (Traffic Cop pattern)
      // Dashboard uses stopImmediatePropagation + preventDefault
      if (e.defaultPrevented) {
        log.debug(' Skipping - event already handled by Dashboard');
        return;
      }
      e.stopPropagation();
      const ideaId = generateBtn.dataset.ideaId;
      openImageGenModal(ideaId);
      return;
    }

    // Preview button - opens preview modal
    const previewBtn = e.target.closest('.btn-preview');
    if (previewBtn) {
      e.stopPropagation();
      const url = previewBtn.dataset.url;
      if (url) {
        openPreviewModal(url);
      }
      return;
    }

    // Download button
    const downloadBtn = e.target.closest('.btn-download');
    if (downloadBtn) {
      e.stopPropagation();
      const url = downloadBtn.dataset.url;
      if (url) {
        downloadImage(url);
      }
      return;
    }

    // Regenerate button (legacy support)
    const regenBtn = e.target.closest('.btn-regenerate');
    if (regenBtn) {
      e.stopPropagation();
      openImageGenModal(regenBtn.dataset.ideaId);
      return;
    }

    // Regenerate image button (new - in image actions)
    const regenImgBtn = e.target.closest('.btn-regenerate-img');
    if (regenImgBtn) {
      e.stopPropagation();
      openImageGenModal(regenImgBtn.dataset.ideaId);
      return;
    }

    // Retry button
    const retryBtn = e.target.closest('.btn-retry');
    if (retryBtn) {
      e.stopPropagation();
      openImageGenModal(retryBtn.dataset.ideaId);
      return;
    }
  });

  // Mark as initialized
  window._imgGenEventsInitialized = true;
}

/**
 * Open image generation modal for an idea
 * For video format, auto-selects Nano Banana provider and specified aspect ratio
 * @param {string} ideaId - The idea ID
 * @param {string|null} overridePrompt - Optional prompt override (from storyboard tab)
 * @param {string|null} overrideAspect - Optional aspect ratio override (from storyboard settings)
 */
export function openImageGenModal(ideaId, overridePrompt = null, overrideAspect = null) {
  const idea = findIdeaById(ideaId);
  if (!idea) {
    log.error('Idea not found:', ideaId);
    return;
  }

  const modal = document.getElementById('image-gen-modal');
  const promptTextarea = document.getElementById('image-gen-prompt');
  const ideaTitleEl = document.getElementById('image-gen-idea-title');

  // Build prompt from idea (or use override from storyboard tab)
  const prompt = overridePrompt || buildPromptFromIdea(idea);
  promptTextarea.value = prompt;

  // Set idea title
  if (ideaTitleEl) {
    ideaTitleEl.textContent = idea.title || '';
  }

  // Store current idea ID
  currentIdeaId = ideaId;

  // ========================================
  // VIDEO FORMAT: Auto-configure for storyboard
  // ========================================
  if (idea.format?.toLowerCase() === 'video') {
    // Select Nano Banana provider (better for storyboard grids)
    const nanoBtn = document.querySelector('.model-btn[data-provider="nano-banana"]');
    if (nanoBtn) {
      document.querySelectorAll('.model-btn').forEach(b => b.classList.remove('active'));
      nanoBtn.classList.add('active');
      updateCostDisplay();
    }

    // Select aspect ratio from storyboard settings, or default to 1:1
    const targetAspect = overrideAspect || '1:1';
    const aspectBtn = document.querySelector(`.aspect-btn[data-ratio="${targetAspect}"]`);
    if (aspectBtn) {
      document.querySelectorAll('.aspect-btn').forEach(b => b.classList.remove('active'));
      aspectBtn.classList.add('active');
    }

    log.debug(' Video format: auto-selected Nano Banana + ' + targetAspect + ' aspect');
  } else {
    // Reset to default model (first provider)
    const firstModelBtn = document.querySelector('.model-btn');
    if (firstModelBtn) {
      document.querySelectorAll('.model-btn').forEach(b => b.classList.remove('active'));
      firstModelBtn.classList.add('active');
      updateCostDisplay();
    }

    // Reset aspect ratio to default 4:3
    const defaultAspectBtn = document.querySelector('.aspect-btn[data-ratio="4:3"]');
    if (defaultAspectBtn) {
      document.querySelectorAll('.aspect-btn').forEach(b => b.classList.remove('active'));
      defaultAspectBtn.classList.add('active');
    }
  }

  // Update context bar with brand/product info
  // Calls updateContextBar immediately, then again after 100ms to catch async context loading
  const updateContext = () => {
    const brief = {
      ...(idea.meta?.brief || {}),
      ...(GeneratorState.brief || {}),
      productData: GeneratorState.brief?.productData || idea.meta?.brief?.productData || GeneratorState.sessionProduct
    };
    const contextData = {
      brand: GeneratorState.currentBrand || GeneratorState.sessionBrand || idea.meta?.brand,
      brief,
      selectedProduct: GeneratorState.selectedProduct || GeneratorState.sessionProduct || brief.productData
    };
    log.debug('Context bar data:', {
      brand: contextData.brand?.name,
      shellColor: contextData.brand?.shell_color,
      selectedProduct: contextData.selectedProduct?.name
    });
    updateContextBar('image-gen', contextData);
  };

  // Update immediately
  updateContext();
  setupContextToggle('image-gen', false);

  // Update again after delay to catch async context loading from Dashboard
  setTimeout(updateContext, 150);

  // Setup toggle listeners to rebuild prompt when brand/product toggles change
  const useBrandCheckbox = document.getElementById('image-gen-use-brand');
  const useProductCheckbox = document.getElementById('image-gen-use-product');

  const rebuildPromptOnToggle = () => {
    // Rebuild prompt with current toggle states
    const newPrompt = buildPromptFromIdea(idea);
    promptTextarea.value = newPrompt;
    log.debug('[Toggle] Rebuilt prompt with ContextState:', { useBrand: ContextState.useBrand, useProduct: ContextState.useProduct });
  };

  // Remove old listeners if any, then add new ones
  if (useBrandCheckbox) {
    useBrandCheckbox._rebuildHandler = rebuildPromptOnToggle;
    useBrandCheckbox.addEventListener('change', rebuildPromptOnToggle);
  }
  if (useProductCheckbox) {
    useProductCheckbox._rebuildHandler = rebuildPromptOnToggle;
    useProductCheckbox.addEventListener('change', rebuildPromptOnToggle);
  }

  // Show modal
  modal.classList.add('active');
  document.body.style.overflow = 'hidden';

  // Focus textarea
  promptTextarea.focus();
}

/**
 * Close image generation modal
 */
export function closeImageGenModal() {
  const modal = document.getElementById('image-gen-modal');
  modal.classList.remove('active');
  document.body.style.overflow = '';
  currentIdeaId = null;
  sceneFrameTarget = null; // Clear scene frame target
  cleanupScope('image-gen-modal'); // Cleanup event listeners
}

/**
 * Open image generation modal for a scene frame
 * @param {string} ideaId - The idea ID
 * @param {number} sceneSeq - The scene sequence number
 * @param {string} frameType - 'start' or 'end'
 * @param {string} prompt - The visual prompt for the scene
 */
export function openImageGenModalForSceneFrame(ideaId, sceneSeq, frameType, prompt) {
  // Store the target
  sceneFrameTarget = { ideaId, sceneSeq, frameType };

  // Find the idea for context
  const idea = findIdeaById(ideaId);

  const modal = document.getElementById('image-gen-modal');
  const promptTextarea = document.getElementById('image-gen-prompt');
  const ideaTitleEl = document.getElementById('image-gen-idea-title');

  // Set the scene prompt
  promptTextarea.value = prompt;

  // Set title to indicate we're generating for a scene frame
  if (ideaTitleEl) {
    ideaTitleEl.textContent = `Scene ${sceneSeq} - ${frameType.toUpperCase()} Frame`;
  }

  // Store current idea ID (needed for the flow)
  currentIdeaId = ideaId;

  // Auto-select 16:9 aspect ratio for scene frames
  const aspectBtn = document.querySelector('.aspect-btn[data-ratio="16:9"]');
  if (aspectBtn) {
    document.querySelectorAll('.aspect-btn').forEach(b => b.classList.remove('active'));
    aspectBtn.classList.add('active');
  }

  // Select Seedream provider (good for scene frames)
  const seedreamBtn = document.querySelector('.model-btn[data-provider="seedream"]');
  if (seedreamBtn) {
    document.querySelectorAll('.model-btn').forEach(b => b.classList.remove('active'));
    seedreamBtn.classList.add('active');
    updateCostDisplay();
  }

  log.debug(' Opening for scene frame:', sceneSeq, frameType);

  // Update context bar with brand/product info
  const updateContext = () => {
    const brief = {
      ...(idea?.meta?.brief || {}),
      ...(GeneratorState.brief || {}),
      productData: GeneratorState.brief?.productData || idea?.meta?.brief?.productData || GeneratorState.sessionProduct
    };
    const contextData = {
      brand: GeneratorState.currentBrand || GeneratorState.sessionBrand || idea?.meta?.brand,
      brief,
      selectedProduct: GeneratorState.selectedProduct || GeneratorState.sessionProduct || brief.productData
    };
    updateContextBar('image-gen', contextData);
  };

  updateContext();
  setupContextToggle('image-gen', false);
  setTimeout(updateContext, 150); // Catch async context loading

  // Show modal
  modal.classList.add('active');
  document.body.style.overflow = 'hidden';
  promptTextarea.focus();
}

/**
 * Check if we're generating for a scene frame
 */
export function isGeneratingForSceneFrame() {
  return sceneFrameTarget !== null;
}

/**
 * Get the scene frame target
 */
export function getSceneFrameTarget() {
  return sceneFrameTarget;
}

/**
 * Get product description from state
 * Checks selectedProduct first (new generations), then sessionProduct (reopened sessions)
 * @returns {string|null} - Product description or null
 */
function getProductDescription() {
  const product = GeneratorState.selectedProduct || GeneratorState.sessionProduct;
  const description = product?.description || null;

  // Debug logging
  log.debug('[ProductDescription] selectedProduct:', GeneratorState.selectedProduct?.name || 'null');
  log.debug('[ProductDescription] sessionProduct:', GeneratorState.sessionProduct?.name || 'null');
  log.debug('[ProductDescription] description:', description?.substring(0, 50) || 'null');

  return description;
}

/**
 * Build prompt from idea data
 * Matches the format from copy-dropdown.js for consistency
 * For video format, builds a storyboard grid prompt
 * @param {Object} idea - The idea object
 * @param {string} aspectRatio - Aspect ratio (default: 4:3, video uses 3:2)
 * @returns {string} - Formatted prompt
 */
function buildPromptFromIdea(idea, aspectRatio = '4:3') {
  const brief = GeneratorState.brief;
  const brand = GeneratorState.currentBrand || GeneratorState.sessionBrand;
  const format = (idea.format || 'print').toLowerCase();

  // Check context toggles - respect user's choices
  const useBrand = ContextState.useBrand;
  const useProduct = ContextState.useProduct;

  log.debug('[buildPromptFromIdea] ContextState:', { useBrand, useProduct });

  // ========================================
  // VIDEO FORMAT: Storyboard Grid Prompt
  // ========================================
  if (format === 'video') {
    return buildStoryboardGridPrompt(idea, brief, useBrand ? brand : null);
  }

  // ========================================
  // OTHER FORMATS: Standard Image Prompt
  // ========================================
  // Format name for prompt prefix
  const formatName = format === 'print' ? 'print ad' :
                     format === 'social' ? 'social media post' :
                     format === 'experiential' ? 'brand activation' :
                     format === 'digital' ? 'digital banner' :
                     format === 'campaign' ? 'campaign visual' : 'ad';

  let prompt = '';

  // Start with the task (what to create)
  prompt += `Create this ${formatName} (${aspectRatio}):\n\n`;
  prompt += `Product: ${brief.product || 'Product'}`;

  // Add product description if toggle is ON (from selected/session product)
  if (useProduct) {
    const productDescription = getProductDescription();
    if (productDescription) {
      prompt += ` - ${productDescription}`;
    }
  }
  prompt += '\n\n';

  // Main visual description
  if (idea.text || idea.visual) {
    prompt += idea.text || idea.visual;
  }

  // Add headline as Headline (skip if [Visual Only])
  if (idea.headline && idea.headline !== '[Visual Only]') {
    prompt += `\n\nHeadline: "${idea.headline}"`;
    if (isValidString(idea.subline)) {
      prompt += `\nSubline: "${idea.subline}"`;
    }
  }

  // V5: APPEND brand visual prompt only if toggle is ON
  if (useBrand && brand && brand.visual_prompt) {
    prompt += `\n\nBrand Style: ${brand.visual_prompt}`;
  }

  return prompt;
}

/**
 * Build storyboard grid prompt for video format
 * Creates a 6-panel storyboard image in a 2x3 grid layout
 * Uses 3:2 aspect ratio for optimal storyboard display
 *
 * @param {Object} idea - The idea object
 * @param {Object} brief - The brief object with product/benefit
 * @param {Object} brand - Optional brand object with visual_prompt (V5)
 * @returns {string} - Storyboard grid prompt
 */
function buildStoryboardGridPrompt(idea, brief, brand = null) {
  const product = brief.product || 'Product';
  // Only include product description if toggle is ON
  const productDescription = ContextState.useProduct ? getProductDescription() : null;
  const productLine = productDescription ? `${product} - ${productDescription}` : product;
  const title = idea.title || 'Untitled';
  const visualDescription = idea.text || idea.visual || '';
  const headline = idea.headline && idea.headline !== '[Visual Only]' ? idea.headline : '';

  // V5: Build brand visual style section if available (brand is already null if toggle is off)
  let brandStyleSection = '';
  if (brand && brand.visual_prompt) {
    brandStyleSection = `\n\nBrand Style: ${brand.visual_prompt}`;
  }

  return `Create a professional 6-panel film storyboard grid.
Layout: 3 rows x 2 columns.
Each panel: Photorealistic cinematic film still, 16:9 panel aspect ratio.
No text, labels, annotations, or frame numbers on panels.

STYLE: Hollywood production quality, anamorphic lens, cinematic color grading, dramatic lighting.

Product: ${productLine}
${title}

${visualDescription}

${headline ? `SUPER: "${headline}"` : ''}

TECHNICAL REQUIREMENTS:
- Consistent lighting and color palette across all 6 panels
- ARRI Alexa / RED camera aesthetic
- Anamorphic lens bokeh
- Professional color grading
- Clean frames, no storyboard overlay
${brandStyleSection}
--ar 3:2 --v 6.1 --style raw`;
}

/**
 * Build simplified video storyboard prompt for auto-generation
 * Used when AUTO_VIDEO_STYLE is set (e.g., '8bit-bw')
 * Avoids conflicting requirements between 8-bit style and cinematic storyboard
 *
 * @param {Object} idea - The video idea object
 * @param {Object} brief - The brief with product info
 * @returns {string} - Simplified storyboard prompt
 */
function buildSimplifiedVideoPrompt(idea, brief) {
  const product = brief.product || 'Product';
  const productDescription = getProductDescription();
  const productLine = productDescription ? `${product} - ${productDescription}` : product;

  // Extract core visual concept from idea
  // Video ideas have structured text with SETUP/TURN/PAYOFF sections
  const visualText = idea.text || idea.visual || '';

  // Try to extract key story beats, or use the full text if not structured
  let visualConcept = visualText;

  // If the visual contains structured sections, extract just the narrative
  const setupMatch = visualText.match(/SETUP:?\s*([^\n]+)/i);
  const turnMatch = visualText.match(/(?:THE\s+)?TURN:?\s*([^\n]+)/i);
  const payoffMatch = visualText.match(/PAYOFF:?\s*([^\n]+)/i);

  if (setupMatch || turnMatch || payoffMatch) {
    const parts = [];
    if (setupMatch) parts.push(setupMatch[1].trim());
    if (turnMatch) parts.push(turnMatch[1].trim());
    if (payoffMatch) parts.push(payoffMatch[1].trim());
    visualConcept = parts.join(' → ');
  }

  // Use title if visual concept is empty
  if (!visualConcept && idea.title) {
    visualConcept = idea.title;
  }

  return `Black and white, 8-bit pixel art style storyboard. Black and white only.
Retro video game aesthetic. Chunky pixels, limited shading.

Product: ${productLine}
${visualConcept}

Requirements:
- Single cohesive image with exactly 6 panels in a 3x2 grid
- Each panel is a distinct scene showing story progression
- 8bit pixel black and white
- No text
- Consistent visual style across all panels
- 1:1 aspect ratio for the overall grid
- Clear panel borders for easy splitting`;
}

/**
 * Refresh prompt textarea with current settings (aspect ratio + style)
 * Called when aspect ratio or style changes
 */
function refreshPromptWithSettings() {
  if (!currentIdeaId) return;

  const idea = findIdeaById(currentIdeaId);
  if (!idea) return;

  const aspectRatio = document.querySelector('.aspect-btn.active')?.dataset.ratio || '4:3';
  const style = document.getElementById('style-selector')?.value || 'auto';
  const promptTextarea = document.getElementById('image-gen-prompt');

  if (promptTextarea) {
    promptTextarea.value = buildStyledPrompt(idea, aspectRatio, style);
  }
}

// ============================================================================
// GENERATION
// ============================================================================

/**
 * Submit image generation from modal
 */
async function submitImageGeneration() {
  log.info('submitImageGeneration called, currentIdeaId:', currentIdeaId);
  if (!currentIdeaId) {
    log.warn('No currentIdeaId, aborting');
    return;
  }

  // Ensure the idea is saved to the database before generating
  // This prevents the "ID mismatch" issue where images can't be linked to unsaved ideas
  const dbId = await ensureIdeaSaved(currentIdeaId);
  if (!dbId) {
    log.error('Failed to ensure idea is saved, cannot proceed with image generation');
    showError('Could not save idea to database. Please try again.', { context: 'image generation' });
    return;
  }
  // Update currentIdeaId to use the database UUID for all subsequent operations
  if (dbId !== currentIdeaId) {
    log.info(' Using database ID for generation:', dbId, '(original:', currentIdeaId, ')');
    // Keep original for UI purposes but use dbId for API calls
  }

  const provider = document.querySelector('.model-btn.active')?.dataset.provider || 'seedream';
  const aspectRatio = document.querySelector('.aspect-btn.active')?.dataset.ratio || '4:3';
  const style = document.getElementById('style-selector')?.value || 'auto';

  // Use the TEXTAREA VALUE directly - user may have edited it
  // Only apply style guide wrapper, don't rebuild the prompt from idea
  const textareaValue = document.getElementById('image-gen-prompt').value;
  const effectiveStyle = getEffectiveStyle(style);
  const styleGuide = STYLE_GUIDES[effectiveStyle] || '';
  const prompt = styleGuide ? `${styleGuide}\n\n${textareaValue}` : textareaValue;

  log.debug('[Submit] Using user prompt from textarea:', textareaValue.substring(0, 100) + '...');

  // Update button state
  const submitBtn = document.getElementById('image-gen-submit');
  const btnText = submitBtn.querySelector('.btn-text');
  const btnLoading = submitBtn.querySelector('.btn-loading');

  btnText.style.display = 'none';
  btnLoading.style.display = 'inline';
  submitBtn.disabled = true;

  try {
    // Submit to queue with fresh auth token
    // NOTE: Previously used cachedAuthToken but this caused issues with long operations
    // Now always fetch fresh token (see VERSION-HISTORY-AUTH-BUGS.md)
    const headers = await getAuthHeaders();
    const response = await fetch(getApiUrl(IMAGE_GEN_ENDPOINTS.submit), {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt, provider, aspectRatio })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    if (data.success) {
      // IMPORTANT: Save ideaId before closing modal (closeImageGenModal sets currentIdeaId to null)
      const ideaId = currentIdeaId;
      // Task 102: Capture frame target BEFORE closing modal (which resets sceneFrameTarget)
      const capturedFrameTarget = sceneFrameTarget ? { ...sceneFrameTarget } : null;
      // Task 102: Generate composite key for concurrent generation tracking
      const genKey = getFrameGenerationKey(ideaId, capturedFrameTarget);

      // REFACTOR-03: Emit image generation started event
      emit(IMAGE_EVENTS.GENERATION_STARTED, {
        ideaId,
        provider,
        aspectRatio,
        requestId: data.requestId
      });

      // Show coin spend animation immediately when request is sent
      const creditCost = CREDIT_COSTS[provider] || 5;
      animateCoinSpend(creditCost);

      // Task 102: Store generation info with composite key and explicit ideaId
      imageGenerations[genKey] = {
        status: 'IN_QUEUE',
        requestId: data.requestId,
        modelId: data.modelId,
        provider: data.provider,
        aspectRatio: aspectRatio,
        cost: data.cost,
        ideaId: ideaId,  // Store ideaId explicitly
        frameTarget: capturedFrameTarget  // Store captured frame target
      };

      // Close modal
      closeImageGenModal();

      // Show placeholder in appropriate location
      if (capturedFrameTarget) {
        // Task 102: Show placeholder for specific frame
        showFramePlaceholder(capturedFrameTarget.sceneSeq, capturedFrameTarget.frameType);
      } else {
        // Show placeholder in card
        showImagePlaceholder(ideaId);
      }

      // Show loading in canvas if open with this idea (only for idea-level generation)
      if (!capturedFrameTarget && window.IdeaCanvas?.isOpen()) {
        const canvasIdea = window.IdeaCanvas.getCurrentIdea();
        if (canvasIdea && String(canvasIdea.id) === String(ideaId)) {
          // Show generating placeholder in canvas left column
          const canvasInner = document.getElementById('canvas-inner');
          if (canvasInner) {
            import('../../../js/shared/idea-canvas/image-form.js').then(module => {
              module.showCanvasGeneratingPlaceholder?.(canvasInner, 'image');
            });
          }
        }
      }

      // Update generate button state
      updateGenerateButton(ideaId, true);

      // Persist generation for page refresh recovery
      saveActiveImageGeneration(genKey, data.modelId, data.requestId, {
        ideaId,
        frameTarget: capturedFrameTarget,
        provider,
        aspectRatio
      });

      // Task 102: Start polling with composite key
      startPolling(genKey, data.modelId, data.requestId);
    } else {
      throw new Error(data.error || 'Generation failed');
    }
  } catch (error) {
    log.error('Image generation error:', error);
    showError(error, { context: 'starting image generation' });
  } finally {
    btnText.style.display = 'inline';
    btnLoading.style.display = 'none';
    submitBtn.disabled = false;
  }
}

/**
 * Start polling for generation result
 * Task 102: Refactored to use composite keys for concurrent generation tracking
 * @param {string} genKey - Composite generation key (ideaId or ideaId_frame_N_type)
 * @param {string} modelId - The model ID
 * @param {string} requestId - The request ID
 */
function startPolling(genKey, modelId, requestId) {
  // Clear any existing polling for this key
  if (pollingIntervals[genKey]) {
    clearInterval(pollingIntervals[genKey]);
  }

  // Task 102: Extract ideaId and frameTarget from stored state
  const state = imageGenerations[genKey];
  const ideaId = state?.ideaId || extractIdeaIdFromFrameKey(genKey);
  const frameTarget = state?.frameTarget;

  pollingIntervals[genKey] = setInterval(async () => {
    try {
      const statusUrl = getApiUrl(IMAGE_GEN_ENDPOINTS.status(modelId, requestId));
      let headers = await getAuthHeaders();
      let response = await fetch(statusUrl, { headers });

      // If 401, clear cached token and retry once with a fresh token
      if (response.status === 401) {
        log.debug('Polling got 401, clearing cached token and retrying...');
        GeneratorState.cachedAuthToken = null;
        headers = await getAuthHeaders();
        response = await fetch(statusUrl, { headers });
      }

      const data = await response.json();

      if (data.status === 'COMPLETED') {
        log.debug(' Generation COMPLETED for genKey:', genKey, 'ideaId:', ideaId);
        // Stop polling
        clearInterval(pollingIntervals[genKey]);
        delete pollingIntervals[genKey];
        // Clear from persistence (no longer needs recovery)
        clearActiveImageGeneration(genKey);

        // Get result
        const resultUrl = getApiUrl(IMAGE_GEN_ENDPOINTS.result(modelId, requestId));
        const resultHeaders = await getAuthHeaders();
        const resultResponse = await fetch(resultUrl, { headers: resultHeaders });
        const resultData = await resultResponse.json();
        log.debug(' Result data:', JSON.stringify(resultData, null, 2));

        // Handle multiple possible response structures:
        // 1. { success: true, data: { images: [...] } } - wrapped by our backend
        // 2. { success: true, data: { data: { images: [...] } } } - double wrapped
        // 3. { images: [...] } - direct fal.ai response
        let images = null;
        if (resultData.data?.data?.images?.length > 0) {
          images = resultData.data.data.images;
          log.debug(' Found images in resultData.data.data.images');
        } else if (resultData.data?.images?.length > 0) {
          images = resultData.data.images;
          log.debug(' Found images in resultData.data.images');
        } else if (resultData.images?.length > 0) {
          images = resultData.images;
          log.debug(' Found images in resultData.images');
        }

        if (images && images.length > 0) {
          const imageUrl = images[0].url;
          log.debug(' Image URL:', imageUrl);

          // Task 102: Use captured frameTarget from state instead of module-level variable
          if (frameTarget) {
            const { sceneSeq, frameType } = frameTarget;
            log.debug(' Saving to scene frame:', sceneSeq, frameType);

            // Save to scene frame
            setSceneFrame(sceneSeq, frameType, {
              url: imageUrl,
              generatedAt: new Date().toISOString(),
              source: 'generated'
            });

            // Task 113: Sync generated frame to Panel Pool for reuse
            // This ensures the image appears in the PANELS tab library
            try {
              addPanelToPool(ideaId, {
                dataUrl: imageUrl,
                url: imageUrl,
                source: 'generated',
                sceneSeq: sceneSeq,
                frameType: frameType,
                createdAt: new Date().toISOString()
              });
              log.debug(' Synced generated frame to Panel Pool');
            } catch (poolErr) {
              log.warn(' Failed to add to panel pool:', poolErr);
            }

            // Re-render storyboard by dispatching custom event
            window.dispatchEvent(new CustomEvent('storyboard:frameUpdated', {
              detail: { ideaId, sceneSeq, frameType, imageUrl }
            }));

            // Update state
            imageGenerations[genKey] = {
              ...imageGenerations[genKey],
              status: 'COMPLETED',
              imageUrl
            };

            // Update generate button state
            updateGenerateButton(ideaId, false);
          } else {
            // Normal idea card image generation
            const genInfo = imageGenerations[genKey] || {};

            // Update state
            imageGenerations[genKey] = {
              ...genInfo,
              status: 'COMPLETED',
              imageUrl
            };

            // Show image in card and save to database
            // Note: showGeneratedImage handles canvas updates, so no need to update here
            showGeneratedImage(ideaId, imageUrl, {
              provider: genInfo.provider || 'seedream',
              aspectRatio: genInfo.aspectRatio || '4:3'
            });

            // Refresh credits from server (animation already shown on submit)
            refreshCreditsFromServer();

            // Update generate button state
            updateGenerateButton(ideaId, false);
          }
        } else {
          log.error(' No images found in result! Structure:', Object.keys(resultData));
          showImageError(ideaId, 'No image returned from API');
          updateGenerateButton(ideaId, false);

          // Refresh canvas if open (to clear loading state)
          if (window.IdeaCanvas?.isOpen()) {
            const canvasIdea = window.IdeaCanvas.getCurrentIdea();
            if (canvasIdea && String(canvasIdea.id) === String(ideaId)) {
              window.IdeaCanvas.refresh();
            }
          }
        }
      } else if (data.status === 'FAILED') {
        // Stop polling
        clearInterval(pollingIntervals[genKey]);
        delete pollingIntervals[genKey];
        // Clear from persistence (no longer needs recovery)
        clearActiveImageGeneration(genKey);

        // Update state
        imageGenerations[genKey] = {
          ...imageGenerations[genKey],
          status: 'FAILED',
          error: data.error || 'Generation failed'
        };

        // Show error
        showImageError(ideaId, data.error || 'Generation failed');

        // Refresh canvas if open (to clear loading state)
        if (window.IdeaCanvas?.isOpen()) {
          const canvasIdea = window.IdeaCanvas.getCurrentIdea();
          if (canvasIdea && String(canvasIdea.id) === String(ideaId)) {
            window.IdeaCanvas.refresh();
          }
        }

        // Update generate button state
        updateGenerateButton(ideaId, false);
      }
      // else still IN_QUEUE or IN_PROGRESS, continue polling

    } catch (error) {
      log.error('Polling error:', error);
    }
  }, 2000); // Poll every 2 seconds
}

// ============================================================================
// UI UPDATES
// ============================================================================

/**
 * Show 8-bit placeholder while generating
 * @param {string} ideaId - The idea ID
 */
function showImagePlaceholder(ideaId) {
  log.debug(' showImagePlaceholder called with ideaId:', ideaId);
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) {
    log.error(' Card not found for placeholder. ideaId:', ideaId);
    return;
  }
  log.debug(' Card found, adding placeholder');

  // Find or create image container
  let imageContainer = card.querySelector('.idea-image-container');
  if (!imageContainer) {
    imageContainer = document.createElement('div');
    imageContainer.className = 'idea-image-container';
    // Insert after header
    const header = card.querySelector('.idea-card-header');
    if (header) {
      header.after(imageContainer);
    }
  }

  imageContainer.innerHTML = `
    <div class="image-placeholder generating">
      <div class="pixel-loader"></div>
      <span class="placeholder-text">GENERATING...</span>
    </div>
  `;
}

/**
 * Show placeholder for a specific scene frame
 * Task 102: Added for concurrent frame generation support
 * @param {number} sceneSeq - Scene sequence number
 * @param {string} frameType - 'start' or 'end'
 */
function showFramePlaceholder(sceneSeq, frameType) {
  const frameContainer = document.querySelector(
    `.scene-card-screenplay[data-scene="${sceneSeq}"] .frame-${frameType}`
  );
  if (frameContainer) {
    frameContainer.innerHTML = `
      <div class="frame-placeholder generating">
        <div class="pixel-loader-sm"></div>
        <span class="generating-text">GENERATING...</span>
      </div>
    `;
  }
  log.debug(` Showing frame placeholder for scene ${sceneSeq} ${frameType}`);
}

/**
 * Save image URL to database with retry logic
 * Updates both the idea record and creates an entry in the images table
 *
 * Features:
 * - 30 second timeout per attempt (prevents hanging)
 * - 3 retry attempts with exponential backoff (1s, 2s, 4s)
 * - Detailed logging for debugging
 *
 * @param {string} ideaId - The idea ID
 * @param {string} imageUrl - The image URL
 * @param {object} meta - Additional metadata (prompt, provider, aspectRatio)
 * @param {object} options - Retry options
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function saveImageToDatabase(ideaId, imageUrl, meta = {}, options = {}) {
  const { maxRetries = 3, timeout = 30000 } = options;
  // NOTE: providedToken option REMOVED - always fetch fresh token to avoid stale token issues
  // during long-running operations (see VERSION-HISTORY-AUTH-BUGS.md)
  const baseDelay = 1000; // 1 second base delay

  log.debug(' saveImageToDatabase called:', { ideaId, imageUrl: imageUrl?.substring(0, 50) + '...' });

  // ═══════════════════════════════════════════════════════════════════
  // FIX: Resolve frontend ID to database UUID
  // Frontend IDs like "A1-1735..." must be mapped to DB UUIDs for version history
  // ═══════════════════════════════════════════════════════════════════
  let dbId = ideaId;

  // Try idMapping in memory first
  if (GeneratorState.idMapping && GeneratorState.idMapping[ideaId]) {
    dbId = GeneratorState.idMapping[ideaId];
    log.debug(` Resolved ID: ${ideaId} → ${dbId}`);
  }
  // Try sessionStorage fallback
  else {
    try {
      const stored = sessionStorage.getItem('cd_id_mapping');
      if (stored) {
        const mapping = JSON.parse(stored);
        if (mapping[ideaId]) {
          dbId = mapping[ideaId];
          log.debug(` Resolved from storage: ${ideaId} → ${dbId}`);
        }
      }
    } catch (e) {
      log.warn(' Could not read id mapping:', e);
    }
  }

  // ALWAYS fetch fresh token at save time - prevents stale token issues during long operations
  // (image generation can take 2+ minutes, token may expire during polling)
  let token = await getSessionToken();

  if (!token) {
    log.warn(' No auth token, skipping DB save for:', ideaId);
    return { success: false, error: 'No auth token' };
  }

  const url = `${API_CONFIG.baseUrl}/api/ideas/${dbId}/image`;
  // Priority: 1) explicit meta.brandId (from refine), 2) current brand, 3) session brand
  const brandId = meta.brandId ||
                  GeneratorState.currentBrand?.id ||
                  GeneratorState.sessionBrand?.id ||
                  null;
  const body = JSON.stringify({
    imageUrl,
    prompt: meta.prompt || '',
    provider: meta.provider || 'seedream',
    aspectRatio: meta.aspectRatio || '4:3',
    brandId  // V5: Link image to brand (supports Dashboard refinement)
  });

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      log.debug(` DB save attempt ${attempt}/${maxRetries} for idea:`, ideaId);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        log.warn(` DB save timeout after ${timeout}ms for idea:`, ideaId);
      }, timeout);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      log.debug(` Response status: ${response.status} for idea:`, ideaId);

      // Handle 401 (token expired) - refresh and retry immediately
      if (response.status === 401) {
        log.warn(' 401 received - attempting token refresh...');
        const { refreshSessionToken } = await import('../../auth/js/client.js');
        const newToken = await refreshSessionToken();
        if (newToken) {
          log.info(' Token refreshed - will retry with new token');
          token = newToken; // Update token for next attempt
          throw new Error('Token expired - retrying with refreshed token');
        } else {
          log.error(' Token refresh failed');
          throw new Error('HTTP 401: Session expired, token refresh failed');
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      if (data.success) {
        log.debug(` ✓ Image saved to database for idea: ${ideaId} (attempt ${attempt})`);
        // Update local timestamp for sorting in Dashboard Recent Ideas
        window.DashboardCore?.Utils?.touchIdea(ideaId);
        return { success: true };
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error) {
      const isTimeout = error.name === 'AbortError';
      const is401Retry = error.message.includes('retrying with refreshed token');
      const errorMsg = isTimeout ? 'Request timeout' : error.message;

      // Don't log 401 retry as an error - it's expected
      if (is401Retry) {
        log.debug(` Token refreshed, will retry (attempt ${attempt}/${maxRetries})`);
      } else {
        log.error(` DB save failed (attempt ${attempt}/${maxRetries}):`, errorMsg);
      }

      // If this was the last attempt, return failure
      if (attempt === maxRetries) {
        log.error(` ✗ All ${maxRetries} attempts failed for idea:`, ideaId);
        return { success: false, error: errorMsg };
      }

      // For 401 retry, no delay needed (token already refreshed)
      // For other errors, use exponential backoff: 1s, 2s, 4s...
      if (!is401Retry) {
        const delay = baseDelay * Math.pow(2, attempt - 1);
        log.debug(` Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  return { success: false, error: 'Max retries exceeded' };
}

/**
 * Upload a temporary URL (fal.media) to R2 for permanent storage
 * @param {string} sourceUrl - The temporary image URL
 * @returns {Promise<{success: boolean, url: string, error?: string}>}
 */
async function uploadToR2ForPermanentStorage(sourceUrl) {
  // Skip if already an R2 URL
  if (sourceUrl.includes('r2.dev') || sourceUrl.includes('r2.cloudflarestorage.com')) {
    log.debug(' URL already permanent, skipping R2 upload');
    return { success: true, url: sourceUrl };
  }

  // Skip non-HTTP URLs
  if (!sourceUrl.startsWith('http://') && !sourceUrl.startsWith('https://')) {
    log.debug(' Skipping R2 upload for non-HTTP URL');
    return { success: true, url: sourceUrl };
  }

  try {
    log.debug(' Uploading to R2 for permanent storage...');

    // Get auth token (prefer cached to avoid timeout issues)
    const token = GeneratorState.cachedAuthToken || await getSessionToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/api/storage/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ sourceUrl })
    });

    const result = await response.json();

    if (result.success) {
      log.debug(' R2 upload success:', result.url);
      return { success: true, url: result.url };
    } else {
      log.warn(' R2 upload failed:', result.error);
      // Return original URL as fallback
      return { success: false, url: sourceUrl, error: result.error };
    }
  } catch (error) {
    log.error(' R2 upload error:', error);
    // Return original URL as fallback
    return { success: false, url: sourceUrl, error: error.message };
  }
}

/**
 * Update the idea canvas if it's open and showing the same idea
 * @param {string} ideaId - The idea ID
 * @param {string} imageUrl - The new image URL
 */
function updateIdeaCanvasImage(ideaId, imageUrl) {
  // Check if IdeaCanvas is available and open
  if (!window.IdeaCanvas?.isOpen?.()) {
    return;
  }

  const currentIdea = window.IdeaCanvas.getCurrentIdea?.();
  if (!currentIdea) {
    return;
  }

  // Check if the canvas is showing the same idea we just updated
  const canvasIdeaId = String(currentIdea.id);
  if (canvasIdeaId !== ideaId) {
    return;
  }

  log.debug(' Updating idea canvas with new image for:', ideaId);

  // Update the idea's imageUrl so the canvas re-renders with the new image
  currentIdea.imageUrl = imageUrl;
  currentIdea.image_url = imageUrl; // Also update snake_case version used by DB

  // Trigger a refresh of the canvas to show the new image
  window.IdeaCanvas.refresh?.();
}

/**
 * Show generated image in card
 * @param {string} ideaId - The idea ID
 * @param {string} imageUrl - The image URL (temporary fal.ai URL)
 * @param {object} meta - Additional metadata for DB save
 */
async function showGeneratedImage(ideaId, imageUrl, meta = {}) {
  log.debug(' showGeneratedImage called:', ideaId, imageUrl);

  // Initialize preloadedImages if needed
  if (!GeneratorState.preloadedImages) {
    GeneratorState.preloadedImages = {};
  }

  // STEP 1: Show image IMMEDIATELY with FAL.ai URL (don't wait for R2!)
  // Backend will queue R2 transfer via saveImageToDatabase -> queueTransfer()
  // FAL.ai URLs expire in 48h which is plenty of time for user session
  GeneratorState.preloadedImages[ideaId] = imageUrl;

  // Task 102: Track latest media timestamp for display priority
  if (!GeneratorState.latestMediaTimestamp) GeneratorState.latestMediaTimestamp = {};
  GeneratorState.latestMediaTimestamp[String(ideaId)] = {
    type: 'image',
    timestamp: Date.now()
  };

  // Persist immediately so timestamp survives page refresh
  persistSessionState();

  // STEP 2: Update UI IMMEDIATELY (don't wait for DB save!)
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) {
    log.error(' Card not found for image. ideaId:', ideaId);
    return;
  }
  log.debug(' Card found, displaying image');

  let imageContainer = card.querySelector('.idea-image-container');
  if (!imageContainer) {
    imageContainer = document.createElement('div');
    imageContainer.className = 'idea-image-container';
    const header = card.querySelector('.idea-card-header');
    if (header) {
      header.after(imageContainer);
    }
  }

  // Use exact same button structure as renderImageSection in phase4-harvest.js
  // Use 'eager' loading since manually regenerated images are already being downloaded
  // Show FAL.ai URL immediately - backend queues R2 transfer in background
  imageContainer.innerHTML = `
    <div class="generated-image-wrapper">
      <img src="${imageUrl}" alt="Generated image" class="generated-image" loading="eager" />
      <div class="image-actions">
        ${renderHistoryButton(ideaId)}
        <button class="btn-img-action btn-preview" data-url="${imageUrl}" title="View Full">
          <svg viewBox="0 0 16 16">
            <rect x="1" y="1" width="4" height="2"/>
            <rect x="1" y="1" width="2" height="4"/>
            <rect x="11" y="1" width="4" height="2"/>
            <rect x="13" y="1" width="2" height="4"/>
            <rect x="1" y="13" width="4" height="2"/>
            <rect x="1" y="11" width="2" height="4"/>
            <rect x="11" y="13" width="4" height="2"/>
            <rect x="13" y="11" width="2" height="4"/>
          </svg>
        </button>
        <button class="btn-img-action btn-download" data-url="${imageUrl}" title="Download">
          <svg viewBox="0 0 12 12">
            <path d="M6 0v8M2 5l4 5 4-5" stroke="black" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <button class="btn-img-action btn-edit" title="Edit Image">
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M12.146 1.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1 0 .708l-9 9a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l9-9zM11.207 2.5L13.5 4.793 12.793 5.5 10.5 3.207 11.207 2.5zm-1.414 1.414L12.5 6.621 6.621 12.5l-2.707-2.707L9.793 3.914zM3.5 11.5l1.5 1.5-2.257.752L3.5 11.5z"/>
          </svg>
        </button>
        <button class="btn-img-action btn-make-video" data-id="${ideaId}" data-image-url="${imageUrl}" title="Make Video">
          <svg viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="2" width="14" height="2"/>
            <rect x="1" y="12" width="14" height="2"/>
            <rect x="1" y="2" width="2" height="12"/>
            <rect x="13" y="2" width="2" height="12"/>
            <rect x="5" y="5" width="2" height="6"/>
            <rect x="7" y="6" width="2" height="4"/>
            <rect x="9" y="7" width="2" height="2"/>
          </svg>
        </button>
      </div>
    </div>
  `;

  // ALSO update the idea canvas if it's open and showing this idea
  updateIdeaCanvasImage(ideaId, imageUrl);

  // REFACTOR-03: Emit events for cross-module communication
  emit(IMAGE_EVENTS.GENERATION_COMPLETED, { ideaId, imageUrl, meta });
  emit(IDEA_EVENTS.IMAGE_GENERATED, { ideaId, imageUrl });

  // STEP 3: Save to database (non-blocking for UI - user already sees the image!)
  // Backend's saveImageToDatabase calls queueTransfer() for background R2 upload
  const saveResult = await saveImageToDatabase(ideaId, imageUrl, meta);
  if (!saveResult.success) {
    log.error(' Failed to save image to database:', saveResult.error);
    // Retry once more with a delay
    setTimeout(async () => {
      const retryResult = await saveImageToDatabase(ideaId, imageUrl, meta);
      if (!retryResult.success) {
        log.error(' Retry also failed:', retryResult.error);
      } else {
        log.debug(' Retry succeeded!');
      }
    }, 2000);
  }
}

/**
 * Show error state in card
 * @param {string} ideaId - The idea ID
 * @param {string} errorMessage - The error message
 */
function showImageError(ideaId, errorMessage) {
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) return;

  let imageContainer = card.querySelector('.idea-image-container');
  if (imageContainer) {
    imageContainer.innerHTML = `
      <div class="image-placeholder error">
        <span class="error-icon">!</span>
        <span class="error-text">${errorMessage}</span>
        <button class="btn-retry" data-idea-id="${ideaId}">RETRY</button>
      </div>
    `;
  }

  // REFACTOR-03: Emit image generation failed event
  emit(IMAGE_EVENTS.GENERATION_FAILED, { ideaId, error: errorMessage });
}

/**
 * Update generate button state
 * @param {string} ideaId - The idea ID
 * @param {boolean} isGenerating - Whether currently generating
 */
function updateGenerateButton(ideaId, isGenerating) {
  const btn = document.querySelector(`.btn-generate-img[data-idea-id="${ideaId}"]`);
  if (!btn) return;

  if (isGenerating) {
    btn.classList.add('generating');
    const textEl = btn.querySelector('.btn-gen-text');
    if (textEl) textEl.textContent = 'GENERATING...';
  } else {
    btn.classList.remove('generating');
    const textEl = btn.querySelector('.btn-gen-text');
    if (textEl) textEl.textContent = 'GENERATE IMAGE';
  }
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Find idea by ID in generated ideas
 * @param {string} ideaId - The idea ID
 * @returns {Object|null} - The idea object or null
 */
function findIdeaById(ideaId) {
  for (const group of GeneratorState.generatedIdeas) {
    const idea = group.ideas.find(i => i.id === ideaId);
    if (idea) return idea;
  }
  return null;
}

/**
 * Check if idea format supports image generation (for UI buttons)
 * All visual formats support manual image generation.
 * @param {string} format - The format code
 * @returns {boolean} - True if format supports image generation
 */
export function supportsImageGeneration(format) {
  // Visual formats that support image generation (for showing generate button)
  const visualFormats = ['print', 'social', 'digital', 'experiential', 'video'];
  const lowerFormat = format?.toLowerCase();
  return visualFormats.includes(lowerFormat);
}

/**
 * Check if idea format should AUTO-generate images during Phase 3
 * Uses AUTO_IMAGE_FORMATS env setting via GeneratorState.imageConfig.autoFormats
 * @param {string} format - The format code
 * @returns {boolean} - True if format should auto-generate images
 */
export function shouldAutoGenerateImage(format) {
  // Get formats from config, fallback to print-only if not configured
  const autoFormats = GeneratorState.imageConfig?.autoFormats || ['print'];
  const lowerFormat = format?.toLowerCase();
  const shouldGenerate = autoFormats.includes(lowerFormat);
  log.debug(`shouldAutoGenerateImage: format="${format}" → "${lowerFormat}", autoFormats=[${autoFormats.join(',')}], result=${shouldGenerate}`);
  return shouldGenerate;
}

/**
 * Get image generation state for an idea
 * @param {string} ideaId - The idea ID
 * @returns {Object|null} - Generation state or null
 */
export function getImageGenerationState(ideaId) {
  return imageGenerations[ideaId] || null;
}

// ============================================================================
// AUTO GENERATION MODE (BLOCKING)
// ============================================================================

/**
 * Build prompt with style guidance
 * @param {Object} idea - The idea object
 * @param {string} aspectRatio - Aspect ratio
 * @param {string} style - Style ID (auto, advertising, random, etc.)
 * @param {boolean} isAutoMode - Whether this is auto-generation mode (Phase 3)
 * @returns {string} - Formatted prompt with style prefix
 */
function buildStyledPrompt(idea, aspectRatio = '4:3', style = 'auto', isAutoMode = false) {
  const format = (idea.format || 'print').toLowerCase();

  // ========================================
  // SPECIAL CASE: Auto-generated video thumbnails
  // Use simplified 8-bit prompt when AUTO_VIDEO_STYLE is configured
  // This avoids conflicting requirements (8-bit + cinematic Hollywood)
  // ========================================
  if (isAutoMode && format === 'video') {
    const autoVideoStyle = GeneratorState.imageConfig?.autoVideoStyle;
    if (autoVideoStyle) {
      log.debug(' Using simplified video prompt (AUTO_VIDEO_STYLE=' + autoVideoStyle + ')');
      return buildSimplifiedVideoPrompt(idea, GeneratorState.brief);
    }
  }

  const basePrompt = buildPromptFromIdea(idea, aspectRatio);

  // Handle 'random' by picking a random style
  const effectiveStyle = getEffectiveStyle(style);
  const styleGuide = STYLE_GUIDES[effectiveStyle] || '';

  if (!styleGuide) {
    return basePrompt;
  }

  return `${styleGuide}\n\n${basePrompt}`;
}

/**
 * AUTO MODE: Generate ALL images and WAIT for completion
 * Returns a Map of ideaId -> imageUrl
 * Used in Phase 3 to block transition until all images are ready
 *
 * Features:
 * - Automatic retry on failure (up to maxRetries)
 * - User feedback during retries
 * - Progress tracking with detailed status
 *
 * @param {Object} config - Image generation config
 * @param {Function} onProgress - Callback for progress updates (status, progress%)
 * @returns {Promise<Map<string, string>>} Map of ideaId to imageUrl
 */
export async function autoGenerateAllAndWait(config, onProgress = () => {}) {
  const {
    provider = 'seedream',
    aspectRatio = '4:3',
    maxConcurrent = 3,
    style = 'auto',
    maxRetries = 2,
    authToken = null  // Pre-fetched token to avoid timeout after long generation
  } = config;

  // Get all idea IDs that should AUTO-generate images (based on AUTO_IMAGE_FORMATS env)
  const allIdeas = [];
  for (const group of GeneratorState.generatedIdeas) {
    for (const idea of group.ideas) {
      if (shouldAutoGenerateImage(idea.format)) {
        allIdeas.push(idea);
      }
    }
  }

  if (allIdeas.length === 0) {
    const autoFormats = GeneratorState.imageConfig?.autoFormats || ['print'];
    log.debug(` No ideas to auto-generate (AUTO_IMAGE_FORMATS=${autoFormats.join(',')})`);
    return new Map();
  }

  const totalImages = allIdeas.length;
  log.debug(` Auto-generating ${totalImages} images (blocking, maxRetries=${maxRetries})...`);
  onProgress(`Generating ${totalImages} images...`, 60);

  // Use provided token (from cached GeneratorState) or fetch fresh
  // This avoids Supabase auth timeout after long generation sessions
  const headers = authToken
    ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }
    : await getAuthHeaders();

  // Track all pending generations with their prompts (for retry)
  const pendingGenerations = new Map(); // ideaId -> { requestId, modelId, prompt }
  const completedImages = new Map();    // ideaId -> imageUrl
  let submittedCount = 0;
  let completedCount = 0;
  let failedCount = 0;
  let retryingCount = 0;

  // Submit in batches
  const batches = [];
  for (let i = 0; i < allIdeas.length; i += maxConcurrent) {
    batches.push(allIdeas.slice(i, i + maxConcurrent));
  }

  // Submit all batches
  for (const batch of batches) {
    const promises = batch.map(async (idea) => {
      // Pass isAutoMode=true to enable simplified video prompt when AUTO_VIDEO_STYLE is set
      const prompt = buildStyledPrompt(idea, aspectRatio, style, true);

      try {
        const response = await fetch(getApiUrl(IMAGE_GEN_ENDPOINTS.submit), {
          method: 'POST',
          headers,
          body: JSON.stringify({ prompt, provider, aspectRatio })
        });

        const data = await response.json();

        if (data.success) {
          // Store prompt for retry capability
          pendingGenerations.set(idea.id, {
            requestId: data.requestId,
            modelId: data.modelId,
            prompt: prompt,
            ideaTitle: idea.title || `Idea ${idea.id}`
          });
          submittedCount++;
          onProgress(`Submitted ${submittedCount}/${totalImages} images...`, 60 + (submittedCount / totalImages) * 10);
        }
      } catch (error) {
        log.error(` Failed to submit ${idea.id}:`, error);
        failedCount++;
      }
    });

    await Promise.all(promises);

    // Small delay between batches
    if (batches.indexOf(batch) < batches.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

  // Poll ALL pending generations until complete (with retry support)
  const pendingCount = pendingGenerations.size;
  onProgress(`Waiting for ${pendingCount} images (this might take a bit)...`, 70);

  const pollPromises = Array.from(pendingGenerations.entries()).map(async ([ideaId, gen], index) => {
    try {
      const imageUrl = await pollUntilComplete({
        modelId: gen.modelId,
        requestId: gen.requestId,
        prompt: gen.prompt,
        provider: provider,
        aspectRatio: aspectRatio,
        headers: headers,  // Auth headers for retry
        maxAttempts: 60,
        maxRetries: maxRetries,
        onRetry: (attempt, max) => {
          retryingCount++;
          const statusMsg = `Retrying image ${index + 1}/${totalImages} (attempt ${attempt}/${max})...`;
          log.debug(` ${statusMsg}`);
          onProgress(statusMsg, 70 + (completedCount / totalImages) * 25);
        }
      });

      if (retryingCount > 0) retryingCount--;

      if (imageUrl) {
        completedImages.set(ideaId, imageUrl);
        completedCount++;
        const progress = 70 + (completedCount / totalImages) * 25;

        if (retryingCount > 0) {
          onProgress(`Images: ${completedCount}/${totalImages} ready, ${retryingCount} retrying...`, progress);
        } else {
          onProgress(`Images: ${completedCount}/${totalImages} ready`, progress);
        }
      } else {
        failedCount++;
        log.warn(` Image failed for idea ${ideaId} after all retries`);
        onProgress(`Image ${index + 1} failed after ${maxRetries} retries`, 70 + (completedCount / totalImages) * 25);
      }
    } catch (error) {
      failedCount++;
      log.error(` Unexpected error for ${ideaId}:`, error);
    }
  });

  await Promise.all(pollPromises);

  // Final summary
  if (failedCount > 0) {
    onProgress(`${completedCount}/${totalImages} images ready, ${failedCount} failed`, 95);
  } else {
    onProgress(`All ${completedCount} images ready!`, 95);
  }

  log.debug(` All images complete: ${completedImages.size}/${totalImages}, failed: ${failedCount}`);
  return completedImages;
}

/**
 * Poll result codes for retry logic
 */
const POLL_RESULT = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  TIMEOUT: 'TIMEOUT'
};

/**
 * Poll a single generation until complete (internal, no retry)
 *
 * @param {string} modelId - fal.ai model ID
 * @param {string} requestId - Request ID to poll
 * @param {number} maxAttempts - Max poll attempts (default: 60 = 2 minutes)
 * @returns {Promise<{status: string, imageUrl?: string}>}
 */
async function doPollOnce(modelId, requestId, maxAttempts = 60) {
  const pollInterval = 2000; // 2 seconds
  const startTime = Date.now();
  const hardTimeoutMs = 180000; // 3 minute hard timeout

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Hard timeout check
    if (Date.now() - startTime > hardTimeoutMs) {
      log.error(` Hard timeout (${hardTimeoutMs}ms) for: ${requestId}`);
      return { status: POLL_RESULT.TIMEOUT };
    }

    try {
      const statusUrl = getApiUrl(IMAGE_GEN_ENDPOINTS.status(modelId, requestId));
      const headers = await getAuthHeaders();
      const response = await fetch(statusUrl, { headers });

      if (!response.ok) {
        log.warn(` Poll HTTP error ${response.status} for ${requestId}`);
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }

      const data = await response.json();

      // Success!
      if (data.status === 'COMPLETED') {
        // Get result
        const resultUrl = getApiUrl(IMAGE_GEN_ENDPOINTS.result(modelId, requestId));
        const resultHeaders = await getAuthHeaders();
        const resultResponse = await fetch(resultUrl, { headers: resultHeaders });
        const resultData = await resultResponse.json();

        // Handle multiple possible response structures
        let images = null;
        if (resultData.data?.data?.images?.length > 0) {
          images = resultData.data.data.images;
        } else if (resultData.data?.images?.length > 0) {
          images = resultData.data.images;
        } else if (resultData.images?.length > 0) {
          images = resultData.images;
        }

        if (images && images.length > 0) {
          return { status: POLL_RESULT.SUCCESS, imageUrl: images[0].url };
        }

        log.error(` No images in completed result: ${requestId}`);
        return { status: POLL_RESULT.FAILED };
      }

      // Permanent failure
      if (data.status === 'FAILED') {
        log.error(` Generation failed: ${requestId}`, data.error || '');
        return { status: POLL_RESULT.FAILED };
      }

      // Still processing, wait and retry
      await new Promise(resolve => setTimeout(resolve, pollInterval));

    } catch (error) {
      log.error(` Poll network error for ${requestId}:`, error.message);
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  log.warn(` Polling timed out after ${maxAttempts} attempts for: ${requestId}`);
  return { status: POLL_RESULT.TIMEOUT };
}

/**
 * Poll a single generation with automatic retry support
 * SAFETY: Hard timeout prevents infinite hangs
 *
 * @param {Object} options - Poll options
 * @param {string} options.modelId - fal.ai model ID
 * @param {string} options.requestId - Request ID to poll
 * @param {string} options.prompt - Original prompt (for retries)
 * @param {string} options.provider - Provider ID (for retries)
 * @param {string} options.aspectRatio - Aspect ratio (for retries)
 * @param {number} options.maxAttempts - Max poll attempts per try (default: 60)
 * @param {number} options.maxRetries - Max retry attempts (default: 2)
 * @param {Function} options.onRetry - Callback when retrying: (attempt, maxRetries) => void
 * @returns {Promise<string|null>} Image URL or null if failed/timeout
 */
async function pollUntilComplete({
  modelId,
  requestId,
  prompt,
  provider,
  aspectRatio,
  headers,  // Auth headers for retry
  maxAttempts = 60,
  maxRetries = 2,
  onRetry = () => {}
}) {
  let currentModelId = modelId;
  let currentRequestId = requestId;

  for (let retry = 0; retry <= maxRetries; retry++) {
    const result = await doPollOnce(currentModelId, currentRequestId, maxAttempts);

    if (result.status === POLL_RESULT.SUCCESS) {
      // === PRELOAD OPTIMIZATION ===
      // Immediately trigger browser download so image is cached before UI renders.
      // This significantly improves perceived loading speed when scrolling through results.
      if (result.imageUrl) {
        const preloadImg = new Image();
        preloadImg.src = result.imageUrl;
        log.debug(` Preloading image to browser cache: ${result.imageUrl.substring(0, 60)}...`);
      }
      return result.imageUrl;
    }

    // Check if we should retry
    if (retry < maxRetries) {
      log.debug(` Retry ${retry + 1}/${maxRetries} for failed image`);
      onRetry(retry + 1, maxRetries);

      // Re-submit to get a new request
      try {
        const response = await fetch(getApiUrl(IMAGE_GEN_ENDPOINTS.submit), {
          method: 'POST',
          headers: headers || { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt, provider, aspectRatio })
        });

        const data = await response.json();

        if (data.success) {
          currentRequestId = data.requestId;
          currentModelId = data.modelId;
          log.debug(` Retry submitted with new requestId: ${currentRequestId}`);
        } else {
          log.error(` Retry submission failed:`, data.error);
          break;
        }
      } catch (error) {
        log.error(` Retry submission error:`, error);
        break;
      }
    }
  }

  log.error(` All retries exhausted for image`);
  return null;
}

/**
 * MANUAL MODE: Generate images for selected ideas (non-blocking, shows placeholders)
 * Used in Phase 4 when user clicks "GENERATE IMAGES" button
 */
export async function generateForSelected() {
  const selectedIds = GeneratorState.selectedIdeas;

  if (selectedIds.length === 0) {
    showError('No ideas selected. Click on cards to select them first.', { context: 'generating images' });
    return;
  }

  const config = GeneratorState.imageConfig;

  // Filter to only image-supported formats
  const validIds = selectedIds.filter(id => {
    const idea = findIdeaById(id);
    return idea && supportsImageGeneration(idea.format);
  });

  if (validIds.length === 0) {
    showError('No selected ideas support image generation (only Print, Social, Digital, Experiential).', { context: 'generating images' });
    return;
  }

  // Get auth headers for requests - always fetch fresh token
  // NOTE: Previously used cachedAuthToken but this caused stale token issues
  // Now always fetch fresh token (see VERSION-HISTORY-AUTH-BUGS.md)
  const headers = await getAuthHeaders();

  // Submit all and show placeholders (non-blocking)
  for (const ideaId of validIds) {
    const idea = findIdeaById(ideaId);
    if (!idea) continue;

    const prompt = buildStyledPrompt(idea, config.autoAspect, config.autoStyle);

    try {
      const response = await fetch(getApiUrl(IMAGE_GEN_ENDPOINTS.submit), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt,
          provider: config.autoProvider,
          aspectRatio: config.autoAspect
        })
      });

      const data = await response.json();

      if (data.success) {
        // Show placeholder in UI
        showImagePlaceholder(ideaId);
        updateGenerateButton(ideaId, true);

        // Persist for page refresh recovery
        saveActiveImageGeneration(ideaId, data.modelId, data.requestId, {
          ideaId,
          frameTarget: null,
          provider: config.autoProvider,
          aspectRatio: config.autoAspect
        });

        // Start polling (updates UI when complete)
        startPolling(ideaId, data.modelId, data.requestId);
      }
    } catch (error) {
      log.error(` Failed to submit ${ideaId}:`, error);
    }
  }
}

// ============================================================================
// IMAGE PREVIEW MODAL
// ============================================================================

// ============================================================================
// IMAGE PREVIEW (uses shared module)
// ============================================================================

/**
 * Initialize image preview modal
 * Now delegates to shared/image-preview.js
 */
export function initImagePreviewModal() {
  initImagePreview();
}

/**
 * Open preview modal with image
 * @param {string} imageUrl - URL of image to preview
 */
export function openPreviewModal(imageUrl) {
  openPreview(imageUrl);
}

/**
 * Download image to user's device
 * Uses centralized download utility with R2 proxy support
 * @param {string} imageUrl - URL of image to download
 * @param {string} filename - Optional filename (defaults to generated name)
 */
export async function downloadImage(imageUrl, filename = null) {
  const finalFilename = filename || `creative-detonator-${Date.now()}`;
  const success = await downloadImageCore(imageUrl, finalFilename);
  if (success) {
    log.debug(' Downloaded image:', finalFilename);
  } else {
    log.warn(' Download fell back to window.open');
  }
}

/**
 * Get image URL for an idea (from preloaded or generated)
 * @param {string} ideaId - The idea ID
 * @returns {string|null} Image URL or null
 */
export function getIdeaImageUrl(ideaId) {
  // 1. Check preloaded images (Auto-gen & Hydrated Reopen)
  if (GeneratorState.preloadedImages && GeneratorState.preloadedImages[ideaId]) {
    return GeneratorState.preloadedImages[ideaId];
  }

  // 2. Check manually generated images (Current Session)
  if (imageGenerations && imageGenerations[ideaId]?.imageUrl) {
    return imageGenerations[ideaId].imageUrl;
  }

  // 3. Fallback: Check the idea object itself (Deep search in Generated Ideas)
  // This handles cases where hydration might have missed or race conditions
  if (GeneratorState.generatedIdeas) {
    for (const group of GeneratorState.generatedIdeas) {
      const idea = group.ideas?.find(i => i.id === ideaId);
      if (idea) {
        return idea.imageUrl || idea.image_url || null;
      }
    }
  }

  return null;
}

// ============================================================================
// GLOBAL EXPORTS (for cross-module access)
// ============================================================================

// Expose openImageGenModal to window for cross-module access
window.openImageGenModal = openImageGenModal;

// Expose openImageGenModalForSceneFrame for storyboard frame generation
window.openImageGenModalForSceneFrame = openImageGenModalForSceneFrame;

// Expose getIdeaImageUrl for storyboard panel import
window.getIdeaImageUrl = getIdeaImageUrl;

